import React, { useState, useEffect } from 'react';
import { Card, CardHeader, Button, Badge, Input, Modal } from '../../../components/ui';
import {
  CreditCard, Plus, Edit, AlertTriangle, CheckCircle,
  XCircle, RefreshCw, Calendar
} from 'lucide-react';
import { adminService, SuscripcionItem, SuscripcionInput, SuscripcionUpdate } from '../services/adminService';

/**
 * SuscripcionesPage — Gestión de suscripciones por banca.
 * 
 * ADMIN controla: plan, estado, vencimiento, monto mensual.
 * Si una banca está VENCIDA o SUSPENDIDA → no puede operar.
 */
export const SuscripcionesPage: React.FC = () => {
  const [suscripciones, setSuscripciones] = useState<SuscripcionItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [editingSub, setEditingSub] = useState<SuscripcionItem | null>(null);
  const [saving, setSaving] = useState(false);

  // Form state
  const [formBancaId, setFormBancaId] = useState('');
  const [formPlan, setFormPlan] = useState('BASICO');
  const [formInicio, setFormInicio] = useState('');
  const [formVencimiento, setFormVencimiento] = useState('');
  const [formMonto, setFormMonto] = useState('');
  const [formEstado, setFormEstado] = useState('ACTIVA');
  const [formObs, setFormObs] = useState('');

  const fetchData = () => {
    setLoading(true);
    setError('');
    adminService.getSuscripciones()
      .then(setSuscripciones)
      .catch(err => setError(err.message || 'Error cargando suscripciones'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { fetchData(); }, []);

  const handleNew = () => {
    setEditingSub(null);
    setFormBancaId('');
    setFormPlan('BASICO');
    setFormInicio(new Date().toISOString().slice(0, 10));
    setFormVencimiento('');
    setFormMonto('');
    setFormEstado('ACTIVA');
    setFormObs('');
    setModalOpen(true);
  };

  const handleEdit = (sub: SuscripcionItem) => {
    setEditingSub(sub);
    setFormBancaId(sub.banca_id);
    setFormPlan(sub.plan);
    setFormInicio(sub.fecha_inicio);
    setFormVencimiento(sub.fecha_vencimiento);
    setFormMonto(sub.monto_mensual);
    setFormEstado(sub.estado);
    setFormObs(sub.observacion);
    setModalOpen(true);
  };

  const handleSave = async () => {
    setSaving(true);
    setError('');
    try {
      if (editingSub) {
        const update: SuscripcionUpdate = {
          plan: formPlan,
          fecha_vencimiento: formVencimiento,
          estado: formEstado,
          monto_mensual: parseFloat(formMonto) || 0,
          observacion: formObs || undefined,
        };
        await adminService.actualizarSuscripcion(editingSub.id, update);
      } else {
        const input: SuscripcionInput = {
          banca_id: formBancaId,
          plan: formPlan,
          fecha_inicio: formInicio,
          fecha_vencimiento: formVencimiento,
          monto_mensual: parseFloat(formMonto) || 0,
          observacion: formObs || undefined,
        };
        await adminService.crearSuscripcion(input);
      }
      setModalOpen(false);
      fetchData();
    } catch (err: any) {
      setError(err.message || 'Error guardando');
    } finally {
      setSaving(false);
    }
  };

  const estadoBadge = (estado: string) => {
    switch (estado) {
      case 'ACTIVA': return <Badge variant="success">ACTIVA</Badge>;
      case 'SUSPENDIDA': return <Badge variant="warning">SUSPENDIDA</Badge>;
      case 'VENCIDA': return <Badge variant="danger">VENCIDA</Badge>;
      default: return <Badge variant="secondary">{estado}</Badge>;
    }
  };

  const estadoIcon = (estado: string) => {
    switch (estado) {
      case 'ACTIVA': return <CheckCircle className="text-success-600" size={18} />;
      case 'SUSPENDIDA': return <AlertTriangle className="text-warning-600" size={18} />;
      case 'VENCIDA': return <XCircle className="text-danger-600" size={18} />;
      default: return null;
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Suscripciones</h1>
          <p className="text-gray-500">Gestión de planes y vencimientos por banca</p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={fetchData}
            className="p-2 text-gray-500 hover:text-primary-600 hover:bg-primary-50 rounded-lg transition-colors"
            title="Refrescar"
          >
            <RefreshCw size={20} className={loading ? 'animate-spin' : ''} />
          </button>
          <Button variant="primary" leftIcon={<Plus size={18} />} onClick={handleNew}>
            Nueva Suscripción
          </Button>
        </div>
      </div>

      {error && (
        <div className="bg-danger-50 border border-danger-200 rounded-xl p-4">
          <div className="flex items-center gap-3">
            <XCircle className="text-danger-600" size={20} />
            <p className="text-danger-800">{error}</p>
          </div>
        </div>
      )}

      {/* Resumen rápido */}
      <div className="grid grid-cols-3 gap-4">
        <Card>
          <div className="text-center">
            <p className="text-sm text-gray-500">Activas</p>
            <p className="text-3xl font-bold text-success-600">
              {suscripciones.filter(s => s.estado === 'ACTIVA').length}
            </p>
          </div>
        </Card>
        <Card>
          <div className="text-center">
            <p className="text-sm text-gray-500">Suspendidas</p>
            <p className="text-3xl font-bold text-warning-600">
              {suscripciones.filter(s => s.estado === 'SUSPENDIDA').length}
            </p>
          </div>
        </Card>
        <Card>
          <div className="text-center">
            <p className="text-sm text-gray-500">Vencidas</p>
            <p className="text-3xl font-bold text-danger-600">
              {suscripciones.filter(s => s.estado === 'VENCIDA').length}
            </p>
          </div>
        </Card>
      </div>

      {/* Tabla de suscripciones */}
      <Card>
        <CardHeader title="Todas las suscripciones" subtitle={`${suscripciones.length} registros`} />
        {loading ? (
          <div className="flex justify-center py-8">
            <RefreshCw className="animate-spin text-gray-400" size={32} />
          </div>
        ) : suscripciones.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            <CreditCard className="mx-auto mb-2 text-gray-300" size={48} />
            <p>No hay suscripciones registradas</p>
            <p className="text-sm">Cree una nueva suscripción para una banca</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b bg-gray-50">
                  <th className="text-left p-3 font-medium text-gray-600">Banca</th>
                  <th className="text-left p-3 font-medium text-gray-600">Plan</th>
                  <th className="text-left p-3 font-medium text-gray-600">Inicio</th>
                  <th className="text-left p-3 font-medium text-gray-600">Vencimiento</th>
                  <th className="text-left p-3 font-medium text-gray-600">Monto</th>
                  <th className="text-left p-3 font-medium text-gray-600">Estado</th>
                  <th className="text-center p-3 font-medium text-gray-600">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {suscripciones.map(sub => (
                  <tr key={sub.id} className="border-b hover:bg-gray-50">
                    <td className="p-3">
                      <div className="flex items-center gap-2">
                        {estadoIcon(sub.estado)}
                        <div>
                          <p className="font-medium">{sub.banca_nombre}</p>
                          <p className="text-xs text-gray-400">{sub.banca_id}</p>
                        </div>
                      </div>
                    </td>
                    <td className="p-3">
                      <Badge variant="primary">{sub.plan}</Badge>
                    </td>
                    <td className="p-3 text-gray-600">{sub.fecha_inicio}</td>
                    <td className="p-3 text-gray-600">{sub.fecha_vencimiento}</td>
                    <td className="p-3 font-medium">₡{parseFloat(sub.monto_mensual).toLocaleString()}</td>
                    <td className="p-3">{estadoBadge(sub.estado)}</td>
                    <td className="p-3 text-center">
                      <button
                        onClick={() => handleEdit(sub)}
                        className="p-2 text-gray-400 hover:text-primary-600 hover:bg-primary-50 rounded-lg"
                        title="Editar"
                      >
                        <Edit size={16} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* Modal crear/editar */}
      <Modal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        title={editingSub ? 'Editar Suscripción' : 'Nueva Suscripción'}
      >
        <div className="space-y-4">
          {!editingSub && (
            <Input
              label="ID de Banca"
              placeholder="Ej: B001"
              value={formBancaId}
              onChange={e => setFormBancaId(e.target.value)}
            />
          )}
          {editingSub && (
            <div className="p-3 bg-gray-50 rounded-lg">
              <p className="text-sm text-gray-500">Banca</p>
              <p className="font-medium">{editingSub.banca_nombre} ({editingSub.banca_id})</p>
            </div>
          )}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Plan</label>
            <select
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
              value={formPlan}
              onChange={e => setFormPlan(e.target.value)}
            >
              <option value="BASICO">Básico</option>
              <option value="PROFESIONAL">Profesional</option>
              <option value="ENTERPRISE">Enterprise</option>
            </select>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <Input
              label="Fecha inicio"
              type="date"
              value={formInicio}
              onChange={e => setFormInicio(e.target.value)}
              disabled={!!editingSub}
            />
            <Input
              label="Fecha vencimiento"
              type="date"
              value={formVencimiento}
              onChange={e => setFormVencimiento(e.target.value)}
            />
          </div>
          <Input
            label="Monto mensual (₡)"
            type="number"
            placeholder="50000"
            value={formMonto}
            onChange={e => setFormMonto(e.target.value)}
          />
          {editingSub && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Estado</label>
              <select
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                value={formEstado}
                onChange={e => setFormEstado(e.target.value)}
              >
                <option value="ACTIVA">ACTIVA</option>
                <option value="SUSPENDIDA">SUSPENDIDA</option>
                <option value="VENCIDA">VENCIDA</option>
              </select>
              <p className="text-xs text-gray-500 mt-1">
                Si cambia a SUSPENDIDA o VENCIDA, la banca será desactivada automáticamente
              </p>
            </div>
          )}
          <Input
            label="Observación (opcional)"
            placeholder="Notas sobre la suscripción"
            value={formObs}
            onChange={e => setFormObs(e.target.value)}
          />

          <div className="flex gap-2 justify-end pt-4">
            <Button variant="outline" onClick={() => setModalOpen(false)}>
              Cancelar
            </Button>
            <Button variant="primary" onClick={handleSave} disabled={saving}>
              {saving ? 'Guardando...' : editingSub ? 'Guardar Cambios' : 'Crear Suscripción'}
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default SuscripcionesPage;
